import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { toast } from "react-hot-toast";
import { track } from "@vercel/analytics";
import Footer from "../../Base/Footer";
import ApiService from "../../services/ApiService";

const buildHalfHourTimeBlocks = (startHour, endHourExclusive) => {
  const blocks = [];
  for (let hour = startHour; hour < endHourExclusive; hour += 1) {
    blocks.push(`${String(hour).padStart(2, "0")}:00`);
    blocks.push(`${String(hour).padStart(2, "0")}:30`);
  }
  return blocks;
};

const AVAILABLE_WEEKDAYS = new Set(["Mon", "Tue", "Wed"]);
const DAY_TIME_BLOCKS_BY_WEEKDAY = {
  Mon: buildHalfHourTimeBlocks(17, 19), // 5:00 PM - 7:00 PM
  Tue: buildHalfHourTimeBlocks(8, 18), // 8:00 AM - 6:00 PM
  Wed: buildHalfHourTimeBlocks(8, 18), // 8:00 AM - 6:00 PM
};

const formatTimeLabel = (time24) => {
  const [hourStr, minute] = (time24 || "").split(":");
  const hour = Number(hourStr);
  if (!Number.isFinite(hour)) return time24;
  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minute} ${suffix}`;
};

const parseMonthDay = (value) => {
  const raw = String(value || "").trim();
  const match = raw.match(/^([A-Za-z]+)\s+(\d{1,2})$/);
  if (!match) return null;

  const monthText = match[1].toLowerCase();
  const day = Number(match[2]);
  if (!Number.isFinite(day)) return null;

  const monthByName = {
    jan: 0,
    january: 0,
    feb: 1,
    february: 1,
    mar: 2,
    march: 2,
    apr: 3,
    april: 3,
    may: 4,
    jun: 5,
    june: 5,
    jul: 6,
    july: 6,
    aug: 7,
    august: 7,
    sep: 8,
    sept: 8,
    september: 8,
    oct: 9,
    october: 9,
    nov: 10,
    november: 10,
    dec: 11,
    december: 11,
  };
  const month = monthByName[monthText];
  if (month === undefined) return null;
  return { month, day };
};

const pad2 = (n) => String(n).padStart(2, "0");

const withAvailabilityWeekday = (option) => {
  const weekdayShort = String(option?.label || "").split(",")[0].trim();
  return {
    ...option,
    weekdayShort,
  };
};

const filterAvailableDateOptions = (options) =>
  options
    .map(withAvailabilityWeekday)
    .filter((option) => AVAILABLE_WEEKDAYS.has(option.weekdayShort));

const getDateOptions = (startDate, endDate, startEpoch, endEpoch) => {
  const daySeconds = 24 * 60 * 60;
  const noonOffsetSeconds = 12 * 60 * 60;
  const lasVegasTz = "America/Los_Angeles";

  const parsedStart = parseMonthDay(startDate);
  const parsedEnd = parseMonthDay(endDate);

  // Prefer explicit month/day strings from event data to avoid timezone drift.
  if (parsedStart && parsedEnd) {
    const baseYear = startEpoch
      ? new Date(startEpoch * 1000).getUTCFullYear()
      : new Date().getUTCFullYear();
    let endYear = baseYear;
    if (
      parsedEnd.month < parsedStart.month ||
      (parsedEnd.month === parsedStart.month && parsedEnd.day < parsedStart.day)
    ) {
      endYear = baseYear + 1;
    }

    const startUtc = new Date(
      Date.UTC(baseYear, parsedStart.month, parsedStart.day, 12, 0, 0)
    );
    const endUtc = new Date(
      Date.UTC(endYear, parsedEnd.month, parsedEnd.day, 12, 0, 0)
    );

    const options = [];
    const cursor = new Date(startUtc);
    while (cursor <= endUtc) {
      const value = `${cursor.getUTCFullYear()}-${pad2(
        cursor.getUTCMonth() + 1
      )}-${pad2(cursor.getUTCDate())}`;
      const label = cursor.toLocaleDateString("en-US", {
        timeZone: lasVegasTz,
        weekday: "short",
        month: "short",
        day: "numeric",
      });
      options.push({ value, label });
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return filterAvailableDateOptions(options);
  }

  // Fallback to epochs if date strings are unavailable.
  if (!startEpoch || !endEpoch) return [];
  const options = [];
  const totalDays = Math.floor((endEpoch - startEpoch) / daySeconds);

  for (let i = 0; i <= totalDays; i += 1) {
    // Epoch dates are saved at ET midnight; shift to midday before PT formatting
    // to avoid rolling back to previous day in Las Vegas time.
    const ts = (startEpoch + i * daySeconds + noonOffsetSeconds) * 1000;
    const d = new Date(ts);
    const value = d.toLocaleDateString("en-CA", {
      timeZone: lasVegasTz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const label = d.toLocaleDateString("en-US", {
      timeZone: lasVegasTz,
      weekday: "short",
      month: "short",
      day: "numeric",
    });
    options.push({ value, label });
  }
  return filterAvailableDateOptions(options);
};

const normalizeStringArray = (values = []) => {
  if (!Array.isArray(values)) return [];
  return [...new Set(values.map((v) => String(v || "").trim()).filter(Boolean))];
};

const toTitleCase = (value) =>
  String(value || "").replace(
    /\w\S*/g,
    (word) => word.charAt(0).toUpperCase() + word.slice(1)
  );

const normalizeCompanyWebsiteUrl = (rawWebsite) => {
  const trimmedWebsite = String(rawWebsite || "").trim();
  if (!trimmedWebsite) return "";

  // Block unsafe schemes in href.
  if (/^(javascript|data):/i.test(trimmedWebsite)) return "";

  if (/^https?:\/\//i.test(trimmedWebsite)) {
    return trimmedWebsite;
  }
  if (/^\/\//.test(trimmedWebsite)) {
    return `https:${trimmedWebsite}`;
  }
  return `https://${trimmedWebsite.replace(/^\/+/, "")}`;
};

export default function Request({ event }) {
  const [isShown, setIsShown] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [ref, inView] = useInView({ triggerOnce: true });

  const [form, setForm] = useState({
    options: [],
    companyOptions: [],
    companyMeetingRequests: {},
    wantsBackToBackMeetings: false,
    wantsAlternateTimeRequest: false,
    alternateTimeRequest: "",
    firstName: "",
    lastName: "",
    company: "",
    jobTitle: "",
    email: "",
    city: "",
    country: "",
    referredBy: "",
    businessGoals: "",
    partners: [],
  });

  const companies = useMemo(() => {
    return Array.isArray(event?.companyOptions) ? event.companyOptions : [];
  }, [event?.companyOptions]);
  const categoryGroups = useMemo(() => {
    const categoriesFromEvent = normalizeStringArray(event?.companyCategories || []);
    const companiesByCategory = {};
    companies.forEach((company) => {
      const category = String(company?.companyCategory || "").trim() || "Other";
      if (!companiesByCategory[category]) {
        companiesByCategory[category] = [];
      }
      companiesByCategory[category].push(company);
    });

    const orderedCategoryNames = [
      ...categoriesFromEvent.filter((category) => companiesByCategory[category]?.length),
      ...Object.keys(companiesByCategory).filter(
        (category) => !categoriesFromEvent.includes(category)
      ),
    ];

    return orderedCategoryNames.map((categoryName, index) => ({
      key: `${categoryName}__${index}`,
      categoryName,
      companies: companiesByCategory[categoryName] || [],
    }));
  }, [companies, event?.companyCategories]);
  const [expandedCategoriesByKey, setExpandedCategoriesByKey] = useState({});
  useEffect(() => {
    if (!categoryGroups.length) {
      setExpandedCategoriesByKey({});
      return;
    }
    setExpandedCategoriesByKey((prev) => {
      const next = {};
      categoryGroups.forEach((group) => {
        const hadValue = Object.prototype.hasOwnProperty.call(prev, group.key);
        next[group.key] = hadValue ? !!prev[group.key] : true;
      });
      return next;
    });
  }, [categoryGroups]);
  const companyMap = useMemo(() => {
    const map = {};
    companies.forEach((c) => {
      map[c.companyId] = c;
    });
    return map;
  }, [companies]);
  const dateOptions = useMemo(
    () =>
      getDateOptions(
        event?.startDate,
        event?.endDate,
        event?.startDateEpoch,
        event?.endDateEpoch
      ),
    [event?.startDate, event?.endDate, event?.startDateEpoch, event?.endDateEpoch]
  );

  const onChangeInput = (input) => (e) => {
    setForm((prev) => ({ ...prev, [input]: e.target.value }));
  };

  const dateOptionWeekdayByValue = useMemo(() => {
    const map = {};
    dateOptions.forEach((option) => {
      map[option.value] = option.weekdayShort;
    });
    return map;
  }, [dateOptions]);

  const getTimeOptionsForDate = (preferredDate) => {
    const weekday = dateOptionWeekdayByValue[preferredDate];
    return DAY_TIME_BLOCKS_BY_WEEKDAY[weekday] || [];
  };

  const toggleCompany = (companyId) => {
    const selected = Array.isArray(form.companyOptions) ? form.companyOptions : [];
    const isSelected = selected.includes(companyId);
    const nextSelected = isSelected
      ? selected.filter((id) => id !== companyId)
      : [...selected, companyId];
    setForm((prev) => {
      const nextRequests = { ...(prev.companyMeetingRequests || {}) };
      if (isSelected) {
        delete nextRequests[companyId];
      } else {
        nextRequests[companyId] = { preferredDate: "", preferredTime: "" };
      }
      return {
        ...prev,
        companyOptions: nextSelected,
        companyMeetingRequests: nextRequests,
        wantsBackToBackMeetings:
          nextSelected.length > 1 ? prev.wantsBackToBackMeetings : false,
      };
    });
  };

  const updateCompanyMeetingRequest = (companyId, field, value) => {
    setForm((prev) => ({
      ...prev,
      companyMeetingRequests: {
        ...(prev.companyMeetingRequests || {}),
        [companyId]: {
          ...(prev.companyMeetingRequests?.[companyId] || {}),
          [field]: value,
        },
      },
    }));
  };

  const updateCompanyMeetingDate = (companyId, preferredDate) => {
    const currentTime =
      form.companyMeetingRequests?.[companyId]?.preferredTime || "";
    const nextAllowedTimes = getTimeOptionsForDate(preferredDate);
    const keepCurrentTime = nextAllowedTimes.includes(currentTime);
    setForm((prev) => ({
      ...prev,
      companyMeetingRequests: {
        ...(prev.companyMeetingRequests || {}),
        [companyId]: {
          ...(prev.companyMeetingRequests?.[companyId] || {}),
          preferredDate,
          preferredTime: keepCurrentTime ? currentTime : "",
        },
      },
    }));
  };

  const clearForm = () => {
    setForm({
      options: [],
      companyOptions: [],
      companyMeetingRequests: {},
      wantsBackToBackMeetings: false,
      wantsAlternateTimeRequest: false,
      alternateTimeRequest: "",
      firstName: "",
      lastName: "",
      company: "",
      jobTitle: "",
      email: "",
      city: "",
      country: "",
      referredBy: "",
      businessGoals: "",
      partners: [],
    });
  };

  const validateForm = () => {
    if (companies.length && !form.companyOptions.length) {
      return "Please select at least one portfolio company";
    }
    if (form.companyOptions.length) {
      const selectedCompanyIds = form.companyOptions;
      const companiesWithCompleteSlot = selectedCompanyIds.filter((companyId) => {
        const req = form.companyMeetingRequests?.[companyId];
        return !!(req && req.preferredDate && req.preferredTime);
      });

      if (form.wantsBackToBackMeetings && selectedCompanyIds.length > 1) {
        if (!companiesWithCompleteSlot.length) {
          return "Please choose at least one preferred day and time";
        }
      } else if (companiesWithCompleteSlot.length !== selectedCompanyIds.length) {
        return "Please choose a preferred day and time for each selected company";
      }
    }
    if (!form.firstName.length) return "Please enter your first name";
    if (!form.lastName.length) return "Please enter your last name";
    if (!form.company.length) return "Please enter your company";
    if (!form.jobTitle.length) return "Please enter your job title";
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!form.email.length || !emailRegex.test(form.email)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const formErrorMsg = validateForm();
  const formValid = !formErrorMsg;
  const submitDisabledClass = formValid ? "" : "disabled";

  const submitForm = async () => {
    if (!formValid) return;

    const payload = {
      ...form,
      eventId: event.eventId,
      optionRequests: [],
      backToBackMeetings:
        !!form.wantsBackToBackMeetings && (form.companyOptions || []).length > 1,
      alternateTimeRequest: String(form.alternateTimeRequest || "").trim(),
      companyOptionRequests: (form.companyOptions || [])
        .map((companyId) => {
          const req = form.companyMeetingRequests?.[companyId] || {};
          if (!req.preferredDate || !req.preferredTime) return null;
          return {
            companyId,
            preferredDate: req.preferredDate || "",
            preferredTime: req.preferredTime || "",
            requestedDuration: "20-30 minutes",
          };
        })
        .filter(Boolean),
    };

    try {
      const api = new ApiService();
      const response = await api.post(`landmark/events/submitGlobalEventRsvp`, {
        form: payload,
      });

      if (response && response.success) {
        setIsShown(true);
        setSubmitError(false);
        clearForm();
      } else {
        throw new Error("Error submitting form");
      }
    } catch (error) {
      setIsShown(false);
      setSubmitError(true);
      toast.error("An error occurred when submitting your response.");
    }
  };

  if (!event || !event.eventId) return null;

  const toggleCategoryExpanded = (categoryKey) => {
    setExpandedCategoriesByKey((prev) => ({
      ...prev,
      [categoryKey]: !prev[categoryKey],
    }));
  };

  return (
    <>
      <div className="cap-width slightly-wider">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 0 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.4, ease: "easeOut" }}
        >
          <div className="form-container event-series-form-container-top" id="apply">
            <div className="requestForm">
              <div className="event-series-request-banner">
                <div className="options-list">
                  <div className="option-container event-series-request-option-container">
                    <div
                      className="option-header logo"
                      style={{
                        backgroundImage: `url(${process.env.PUBLIC_URL}/images/deal/option-header-bg.jpg)`,
                      }}
                    >
                      <div className="top mt">
                        <div className="text">
                          MEET OUR PORTFOLIO COMPANIES
                        </div>
                        <div className="text postfix event-series-request-postfix-primary">
                          Black Hat Week | August 3–6
                        </div>
                        <div className="text postfix">
                          Mandalay Bay, Las Vegas
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="partner-text">
                Landmark Ventures is hosting a series of curated 1:1 meetings
                during Black Hat week, connecting security leaders with a select
                group of innovative companies across the cybersecurity ecosystem.
              </p>
              <p className="partner-text">
                Browse the companies below and select who you would like to
                meet. Our team will coordinate tailored introductions based on
                your interests and availability.
              </p>

              <div className="event-series-company-section">
                <div className="event-series-company-title">
                  Request Meetings
                </div>
                <div className="event-series-company-subtitle">
                  Select one or more companies and we'll work to arrange
                  introductions during Black Hat week.
                </div>
                <div className="event-series-company-subtitle">
                  Please allow <strong>20 minutes per meeting</strong> when
                  selecting your availability to ensure smooth scheduling.
                </div>
              </div>
            </div>
          </div>

          <div className="event-series-company-categories-block">
            {categoryGroups.length ? (
              <CompactCompanyCheckboxes
                categoryGroups={categoryGroups}
                expandedCategoriesByKey={expandedCategoriesByKey}
                onToggleCategory={toggleCategoryExpanded}
                selectedIds={form.companyOptions}
                onToggle={toggleCompany}
              />
            ) : (
              <div className="event-series-company-empty">
                Portfolio companies will be announced soon.
              </div>
            )}
          </div>

          <div className="form-container event-series-form-container-bottom">
            <div className="requestForm">
              <h3 style={{ marginTop: "40px" }}>Your Information</h3>

              <div className="requestForm__inputs">
                <label className="input__outer sm">
                  <h5>First Name</h5>
                  <div className="input">
                    <input
                      type="name"
                      value={form.firstName}
                      onChange={onChangeInput("firstName")}
                    />
                  </div>
                </label>

                <label className="input__outer sm">
                  <h5>Last Name</h5>
                  <div className="input">
                    <input
                      type="text"
                      value={form.lastName}
                      onChange={onChangeInput("lastName")}
                    />
                  </div>
                </label>

                <label className="input__outer sm">
                  <h5>Company</h5>
                  <div className="input">
                    <input
                      type="text"
                      value={form.company}
                      onChange={onChangeInput("company")}
                    />
                  </div>
                </label>

                <label className="input__outer sm">
                  <h5>Job title</h5>
                  <div className="input">
                    <input
                      type="text"
                      value={form.jobTitle}
                      onChange={onChangeInput("jobTitle")}
                    />
                  </div>
                </label>

                <label className="input__outer sm">
                  <h5>Email</h5>
                  <div className="input">
                    <input
                      type="email"
                      value={form.email}
                      onChange={onChangeInput("email")}
                    />
                  </div>
                </label>

                <label className="input__outer sm">
                  <h5>Referred By (optional)</h5>
                  <div className="input">
                    <input
                      type="text"
                      value={form.referredBy}
                      onChange={onChangeInput("referredBy")}
                    />
                  </div>
                </label>

                <label className="input__outer">
                  <h5>Business Goals (optional)</h5>
                  <div className="input">
                    <input
                      type="text"
                      value={form.businessGoals}
                      onChange={onChangeInput("businessGoals")}
                    />
                  </div>
                </label>
              </div>

              {form.companyOptions.length ? (
                <div className="event-series-meeting-preferences">
                  <div className="event-series-meeting-preferences-title">
                    Preferred meeting day and time
                  </div>
                  <div className="event-series-meeting-preferences-note">
                    Each meeting typically takes 20-30 minutes. Please select a
                    preferred day and time for each company, or select the
                    back-to-back option and provide at least one preferred slot.
                  </div>
                  <div className="event-series-meeting-preferences-list">
                    {form.companyOptions.map((companyId) => {
                      const req = form.companyMeetingRequests?.[companyId] || {
                        preferredDate: "",
                        preferredTime: "",
                      };
                      return (
                        <div
                          className="event-series-meeting-preference-row"
                          key={`slot-${companyId}`}
                        >
                          <div className="event-series-meeting-company-name">
                            {companyMap[companyId]?.companyName || companyId}
                          </div>
                          <div className="event-series-meeting-selects">
                            {dateOptions.length ? (
                              <select
                                value={req.preferredDate}
                                onChange={(e) =>
                                  updateCompanyMeetingDate(
                                    companyId,
                                    e.target.value
                                  )
                                }
                              >
                                <option value="">Select day</option>
                                {dateOptions.map((d) => (
                                  <option
                                    key={`${companyId}-${d.value}`}
                                    value={d.value}
                                  >
                                    {d.label}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="date"
                                value={req.preferredDate}
                                onChange={(e) =>
                                  updateCompanyMeetingRequest(
                                    companyId,
                                    "preferredDate",
                                    e.target.value
                                  )
                                }
                              />
                            )}
                            <select
                              value={req.preferredTime}
                              onChange={(e) =>
                                updateCompanyMeetingRequest(
                                  companyId,
                                  "preferredTime",
                                  e.target.value
                                )
                              }
                            >
                              <option value="">Select time</option>
                              {getTimeOptionsForDate(req.preferredDate).map((t) => (
                                <option key={`${companyId}-${t}`} value={t}>
                                  {formatTimeLabel(t)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {form.companyOptions.length > 1 ? (
                    <label className="event-series-back-to-back-toggle">
                      <input
                        type="checkbox"
                        checked={!!form.wantsBackToBackMeetings}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            wantsBackToBackMeetings: e.target.checked,
                          }))
                        }
                      />
                      <span>
                        Please select here if you would like the meetings to be
                        scheduled back-to-back.
                      </span>
                    </label>
                  ) : null}
                  <label className="event-series-custom-time-toggle">
                    <input
                      type="checkbox"
                      checked={!!form.wantsAlternateTimeRequest}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setForm((prev) => ({
                          ...prev,
                          wantsAlternateTimeRequest: checked,
                          alternateTimeRequest: checked
                            ? prev.alternateTimeRequest
                            : "",
                        }));
                      }}
                    />
                    <span>
                      Please select if you want to request a time not shown above.
                    </span>
                  </label>
                  {form.wantsAlternateTimeRequest ? (
                    <label className="event-series-custom-time-note">
                      <span>Please let us know what time you'd prefer:</span>
                      <textarea
                        value={form.alternateTimeRequest}
                        onChange={onChangeInput("alternateTimeRequest")}
                        rows={3}
                      />
                    </label>
                  ) : null}
                </div>
              ) : null}

              <p className="partner-text event-series-followup-note">
                After submitting your request, you will receive an email from{" "}
                <strong>Fernanda Bishop</strong> (fcalderon@landmarkventures.com)
                {" "}to coordinate and confirm your meetings.
              </p>

              <div className="requestForm submit">
                {!isShown && (
                  <div className="submit-wrapper">
                    <button
                      type="submit"
                      onClick={submitForm}
                      className={`button pink-btn ${submitDisabledClass}`}
                    >
                      SUBMIT
                    </button>
                    {formErrorMsg.length ? (
                      <div className="form-invalid-msg text-center">
                        {formErrorMsg}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              <AnimatePresence>
                {isShown && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className="submit-success-msg">
                      Thank you — your request has been received.
                      <br />
                      Our team will review and follow up with confirmation and
                      logistic details if we are able to accommodate you.
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {submitError && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className="submit-success-msg">
                      There was an error when submitting your form. <br />
                      Please reach out to <b>scui@landmarkventures.com</b> and
                      the team will help log your request.
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="footer-section" style={{ marginTop: "70px" }}></div>
      <div className="ed-footer">
        <Footer alternate={true} />
      </div>
    </>
  );
}

const CompactCompanyCheckboxes = ({
  categoryGroups,
  expandedCategoriesByKey,
  onToggleCategory,
  selectedIds,
  onToggle,
}) => {
  if (!Array.isArray(categoryGroups) || !categoryGroups.length) return null;
  const selectedCompanyIds = Array.isArray(selectedIds) ? selectedIds : [];

  return (
    <div className="event-series-category-groups">
      {categoryGroups.map((categoryGroup) => {
        const expanded = !!expandedCategoriesByKey[categoryGroup.key];
        return (
          <div className="event-series-category-group" key={categoryGroup.key}>
            <button
              type="button"
              className={`event-series-category-header ${expanded ? "expanded" : ""}`}
              onClick={() => onToggleCategory(categoryGroup.key)}
            >
              <span className="event-series-category-header-title">
                {categoryGroup.categoryName}
              </span>
              <span className="event-series-category-header-right">
                <div className="event-series-category-count-circle">
                  <div className="event-series-category-count-text">
                    {categoryGroup.companies.length}
                  </div>
                </div>
                <span className="event-series-category-chevron">⌄</span>
              </span>
            </button>

            <AnimatePresence initial={false}>
              {expanded ? (
                <motion.div
                  className="event-series-category-panel"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <div className="event-series-compact-company-list mobile-summary-stacked event-series-category-company-grid">
                    {categoryGroup.companies.map((company) => {
                      const checked = selectedCompanyIds.includes(company.companyId);
                      const companyTags = normalizeStringArray(company.companyTags || []);
                      const companyWebsiteUrl = normalizeCompanyWebsiteUrl(
                        company.companyWebsite
                      );
                      return (
                        <div
                          key={company.companyId}
                          className={`event-series-compact-company-row ${
                            checked ? "selected" : ""
                          }`}
                          onClick={() => onToggle(company.companyId)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              onToggle(company.companyId);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div className="event-series-company-checkbox">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => onToggle(company.companyId)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="event-series-company-checkbox-ui">
                              <img
                                src={process.env.PUBLIC_URL + "/images/icons/tick-white.svg"}
                                alt="selected"
                              />
                            </span>
                          </div>

                          <div className="event-series-compact-company-text">
                            <div className="event-series-company-name-line">
                              <div className="event-series-company-title-wrap">
                                {companyWebsiteUrl ? (
                                  <a
                                    className="event-series-company-title-link"
                                    href={companyWebsiteUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      track("Company URL Click", {
                                        companyId: company.companyId,
                                        companyName: company.companyName,
                                        companyUrl: companyWebsiteUrl,
                                      });
                                    }}
                                  >
                                    <span className="event-series-company-name">
                                      {company.companyName}
                                    </span>
                                  </a>
                                ) : (
                                  <span className="event-series-company-name">
                                    {company.companyName}
                                  </span>
                                )}
                              </div>
                            </div>

                            {company.companySummary ? (
                              <div className="event-series-company-summary-row">
                                <div className="event-series-company-summary-inline">
                                  <div
                                    className="event-series-company-summary-sep"
                                    aria-hidden="true"
                                  ></div>
                                  <div className="event-series-company-summary-text">
                                    {company.companySummary}
                                  </div>
                                </div>
                              </div>
                            ) : null}

                            {company.companyDescription ? (
                              <div className="event-series-company-description">
                                {company.companyDescription}
                              </div>
                            ) : null}

                            {companyTags.length ? (
                              <div className="event-series-company-tag-row">
                                {companyTags.map((tag, tagIdx) => (
                                  <span
                                    key={`${company.companyId}-${tag}-${tagIdx}`}
                                    className={`event-series-company-tag event-series-company-tag-${tagIdx % 4}`}
                                  >
                                    <span className="event-series-company-tag-text">
                                      {toTitleCase(tag)}
                                    </span>
                                  </span>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};
